import type { ApiConfig, DomainConfig, Language, LoyaltyAuthenticationConfig } from '@vista-digital/occ-domain'
import { LoyaltyAuthenticationType, LoyaltyMemberIdentifier } from '@vista-digital/occ-domain'
import type { OccConfig } from '@vista-digital/occ-ui'
import { CaptchaType } from '@vista-digital/occ-ui'
import type { NestedPartial } from '@vista-digital/utilities'
import { useMemo } from 'react'
import { LanguageTagEnum } from '@/common/enums/languageTag.enum'
import { CmsCountries } from '@/content/services/cms.data.service'
import { useMobileEmbedToken } from '@/content/services/mobileEmbed/hooks/useMobileEmbedToken'
import { mobileEmbedService } from '@/content/services/mobileEmbed/mobileEmbed.service'
import type { InitialDataResponse } from '@/content/services/models/initialDataResponse'
import type { OccConfigurationOverridePropsApi } from '../occConfigurationOverride/models'
import type { OccContextPropsApi } from './models'
import {
    getAssets,
    getFilmConfig,
    getFilterConfig,
    getFormValidationConfig,
    getMemberConfig,
    getNotificationConfig,
    getOccAccessibilityText,
    getOccContextDisplayText,
    getOccLocale,
    getOrderConfig,
} from './props'

export const useOmniaOccProps = (
    data: InitialDataResponse,
    occConfigurationOverride: Partial<Omit<OccConfigurationOverridePropsApi, 'vistaCinema'>> = {},
    isLivingTicketPage: boolean = false,
    onSignedOut: () => void = () => {},
    onTokenExpired: () => Promise<void> = () => Promise.resolve(),
    authentication?: NestedPartial<LoyaltyAuthenticationConfig>
) => {
    const api: NestedPartial<ApiConfig> = useMemo(() => {
        return {
            url: data.api.apiUrl,
            authTokenProvider: async (refresh: boolean) => {
                if (refresh) {
                    await onTokenExpired()
                }

                if (mobileEmbedService.isMobileApp() && mobileEmbedService.GasToken) {
                    return mobileEmbedService.GasToken
                }

                return data.api.authToken
            },
            regionCode: data.api.regionCode,
        }
    }, [data.api.apiUrl, data.api.authToken, data.api.regionCode, onTokenExpired])

    const cdn = useMemo(() => {
        return {
            vista: { url: data.api.vistaCdn },
            moviexchange: { url: data.api.movieExchangeCdn },
        }
    }, [data.api.movieExchangeCdn, data.api.vistaCdn])

    const languages = useMemo((): Language[] => {
        switch (data?.country) {
            case CmsCountries.Spain:
                return [
                    {
                        languageTag: LanguageTagEnum.Spanish,
                        label: 'Spanish',
                        iconName: 'spain-flag',
                        isDefault: true,
                    },
                ]
            case CmsCountries.Portugal:
                return [
                    {
                        languageTag: LanguageTagEnum.Portuguese,
                        label: 'Portuguese',
                        iconName: 'portugal-flag',
                        isDefault: true,
                    },
                ]
            case CmsCountries.Finnkino:
                return [
                    {
                        languageTag: data.culture,
                        label: 'Finnkino',
                        iconName: 'finnish-flag',
                        isDefault: true,
                    },
                ]
            case CmsCountries.UnitedKingdom:
            case CmsCountries.Ireland:
            default:
                return [
                    {
                        languageTag: LanguageTagEnum.English,
                        label: 'English',
                        iconName: 'united-kingdom-flag',
                        isDefault: true,
                    },
                ]
        }
    }, [data.country, data.culture])

    const occConfiguration: OccContextPropsApi = useMemo(() => {
        return {
            ...data.occConfiguration,
            ...occConfigurationOverride,
        }
    }, [data.occConfiguration, occConfigurationOverride])

    const { getMobileAppToken, updateMobileAppToken } = useMobileEmbedToken()

    const domain = useMemo(() => {
        return {
            api: api,
            languages: languages,
            browsing: {
                maximumSelectedSites: occConfiguration.maximumNumberOfSelectedCinemas,
            },
            cache: {
                durations: {
                    // Changed as part of COMS-15656, to be reverted with OCC 15 upgrade
                    showtimes: 20,
                    films: 20,
                    items: 20,
                    sites: 20,
                    tickets: 20,
                    member: 20,
                    loyaltyMasterData: 20,
                    seatingMasterData: 20,
                    subscriptions: 20,
                    events: 20,
                    orders: 20,
                    paymentMethods: 20,
                    journeys: isLivingTicketPage ? 0 : 20,
                },
            },
            cdn,
            // TODO: see what tot do with this, as ticket redemption code types are now loaded async in the redemption code component, and not part of the occ configuration
            // ticketRedemptionCodeTypes: getTicketRedemptionCodeTypes(),
            cxm: data?.cxmConfiguration ?? { enabled: false },
            loyalty: {
                authentication:
                    authentication ??
                    (!getMobileAppToken()
                        ? {
                              type: LoyaltyAuthenticationType.Cookie,
                              memberIdentifier: LoyaltyMemberIdentifier.Email,
                          }
                        : {
                              type: LoyaltyAuthenticationType.Token,
                              setToken: updateMobileAppToken,
                              getToken: getMobileAppToken,
                          }),
                onSignedOut: onSignedOut,
            },
            order: getOrderConfig(occConfiguration),
        } as NestedPartial<DomainConfig>
    }, [
        api,
        cdn,
        data?.cxmConfiguration,
        isLivingTicketPage,
        languages,
        occConfiguration,
        onSignedOut,
        getMobileAppToken,
        updateMobileAppToken,
        authentication,
    ])

    const config: NestedPartial<OccConfig> = useMemo(() => {
        return {
            enableSkeletonLoadingIndicators: true,
            animation: {
                enableAnimations: true,
            },
            locale: getOccLocale(occConfiguration),
            security: {
                captcha: data.captcha.siteKey
                    ? { siteKey: data.captcha.siteKey, type: CaptchaType.GoogleRecaptchaV2 }
                    : undefined,
            },
            form: {
                enableFloatingInputLabels: false,
                fieldValidation: getFormValidationConfig(occConfiguration),
            },
            film: {
                ...getFilmConfig(occConfiguration),

                // Custom film actions require loading data async, which would trigger a rerender and a flicker of the whole page if loaded in the occ context.
                // Instead, they are loaded in PageWrapper.tsx when the first page load is detected, and then set directly using useOccConfig().film.customActions
                customActions: {},
            },
            filter: getFilterConfig(occConfiguration),
            member: getMemberConfig(occConfiguration),
            notification: getNotificationConfig(occConfiguration),
            // showtime:
        } as NestedPartial<OccConfig>
    }, [data.captcha, occConfiguration])

    const displayText = useMemo(
        () => getOccContextDisplayText(occConfiguration, data.country),
        [occConfiguration, data.country]
    )
    const accessibilityText = useMemo(() => getOccAccessibilityText(occConfiguration), [occConfiguration])
    const assets = useMemo(() => getAssets(occConfiguration), [occConfiguration])

    return {
        domain,
        config,
        displayText,
        accessibilityText,
        assets,
    }
}
